import React, { useEffect, useState, useRef } from 'react';
import { GMap } from 'primereact/gmap';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { loadGoogleMapsEmpleado } from './GoogleMapsEmpleado';

export default function SelectorUbicacion({
    coordenadaInicial,
    onConfirmar,
    onCancelar
}) {
    const [mapReady, setMapReady] = useState(false);
    const [overlays, setOverlays] = useState([]);
    const [selectedCoord, setSelectedCoord] = useState(null);
    const [coordInput, setCoordInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const [mapOptions, setMapOptions] = useState({
        center: { lat: -0.180653, lng: -78.467834 },
        zoom: 15,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true
    });

    const toast = useRef(null);
    const inputSearchRef = useRef(null);

    // --- LOGICA DE SELECCIÓN REAL (PIN ROJO) ---
    const establecerSeleccionReal = (position, moveMap = false) => {
        if (!window.google) return;

        const seleccionMarker = new window.google.maps.Marker({
            position: position,
            title: "Ubicación confirmada",
            animation: window.google.maps.Animation.DROP,
            // Pin rojo estándar (por defecto)
        });

        setSelectedCoord(position);
        setCoordInput(`${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`);

        // Actualizamos overlays manteniendo el pin de búsqueda si existiera
        setOverlays(prev => {
            // Quitamos cualquier marcador de selección previo (el rojo)
            const sinSeleccionAnterior = prev.filter(ov => ov.get('tipo') !== 'seleccion');
            seleccionMarker.set('tipo', 'seleccion');
            return [...sinSeleccionAnterior, seleccionMarker];
        });

        if (moveMap) {
            setMapOptions(prev => ({ ...prev, center: position }));
        }
    };

    // --- LOGICA DE BÚSQUEDA TEMPORAL (PIN AZUL) ---
    const mostrarPinBusqueda = (position) => {
        if (!window.google) return;

        const buscadorMarker = new window.google.maps.Marker({
            position: position,
            title: "Lugar encontrado",
            icon: {
                url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png" // PIN AZUL
            }
        });

        setOverlays(prev => {
            // Quitamos pines de búsqueda anteriores
            const sinBusquedaAnterior = prev.filter(ov => ov.get('tipo') !== 'busqueda');
            buscadorMarker.set('tipo', 'busqueda');
            return [...sinBusquedaAnterior, buscadorMarker];
        });

        // Siempre movemos el mapa al buscar
        setMapOptions(prev => ({ ...prev, center: position }));
    };

    useEffect(() => {
        loadGoogleMapsEmpleado(() => {
            setMapReady(true);
            
            setTimeout(() => {
                const inputElement = inputSearchRef.current?.getElement?.() || inputSearchRef.current;

                if (inputElement && window.google) {
                    const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
                        fields: ["geometry", "formatted_address"],
                        types: ["geocode", "establishment"]
                    });

                    autocomplete.addListener("place_changed", () => {
                        const place = autocomplete.getPlace();
                        if (place.geometry && place.geometry.location) {
                            const newPos = {
                                lat: place.geometry.location.lat(),
                                lng: place.geometry.location.lng()
                            };
                            // AQUÍ ESTÁ EL CAMBIO: Solo muestra el pin azul
                            mostrarPinBusqueda(newPos);
                            setSearchQuery(place.formatted_address);
                        }
                    });

                    window.google.maps.event.addDomListener(inputElement, 'keydown', (e) => {
                        if (e.keyCode === 13) e.preventDefault();
                    });
                }
            }, 600);

            if (coordenadaInicial && coordenadaInicial !== "0.0,0.0" && coordenadaInicial !== "") {
                const parts = coordenadaInicial.split(',');
                const lat = parseFloat(parts[0]);
                const lng = parseFloat(parts[1]);
                if (!isNaN(lat) && !isNaN(lng)) {
                    establecerSeleccionReal({ lat, lng }, true);
                }
            }
        });
    }, [coordenadaInicial]);

    const handleMapClick = (event) => {
        const position = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        // El clic en el mapa siempre es la selección real (Pin Rojo)
        establecerSeleccionReal(position, false);
    };

    const handleSave = () => {
        if (!selectedCoord) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Haz clic en el mapa para marcar la ubicación.' });
            return;
        }
        onConfirmar(`${selectedCoord.lat},${selectedCoord.lng}`);
    };

    const limpiarBusqueda = () => {
        setSearchQuery("");
        setOverlays(prev => prev.filter(ov => ov.get('tipo') !== 'busqueda'));
    };

    // Aplica las coordenadas tipeadas manualmente en el input (formato "lat, lng").
    // Pone el pin rojo en esa posicion y centra el mapa.
    const aplicarCoordenadaManual = () => {
        if (!coordInput || !coordInput.trim()) return;
        const partes = coordInput.split(',').map(p => p.trim());
        if (partes.length < 2) {
            toast.current.show({ severity: 'warn', summary: 'Formato invalido', detail: 'Usa el formato: latitud, longitud (ej. -2.245030, -79.893082)' });
            return;
        }
        const lat = parseFloat(partes[0]);
        const lng = parseFloat(partes[1]);
        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            toast.current.show({ severity: 'warn', summary: 'Coordenada invalida', detail: 'La latitud debe estar entre -90 y 90, la longitud entre -180 y 180.' });
            return;
        }
        establecerSeleccionReal({ lat, lng }, true);
    };

    const handleCoordKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            aplicarCoordenadaManual();
        }
    };

    if (!mapReady) return <div className="p-5 text-center"><i className="pi pi-spin pi-spinner mr-2"></i>Cargando Mapa...</div>;

    return (
        <div className="flex flex-column gap-3">
            <Toast ref={toast} />

            <div className="p-inputgroup flex-1">
                <span className="p-inputgroup-addon"><i className="pi pi-search"></i></span>
                <InputText
                    ref={inputSearchRef}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Busca para orientarte en el mapa..."
                    className="p-inputtext-sm"
                />
                <Button type="button" icon="pi pi-times" className="p-button-danger p-button-outlined" onClick={limpiarBusqueda} />
            </div>

            <div className="surface-border border-2 border-round p-1 shadow-2">
                <GMap
                    key={`${mapOptions.center.lat}-${mapOptions.center.lng}`}
                    options={mapOptions}
                    overlays={overlays}
                    style={{ width: '100%', height: '400px' }}
                    onMapClick={handleMapClick}
                />
            </div>

            <div className="flex flex-column md:flex-row justify-content-between align-items-center gap-3">
                <div className="p-inputgroup flex-1">
                    <span className="p-inputgroup-addon cursor-pointer" onClick={() => selectedCoord && setMapOptions(p => ({...p, center: selectedCoord}))}>
                        <i className="pi pi-map-marker text-red-500"></i>
                    </span>
                    <InputText
                        value={coordInput}
                        onChange={(e) => setCoordInput(e.target.value)}
                        onKeyDown={handleCoordKeyDown}
                        placeholder="Latitud, Longitud (ej. -2.245030, -79.893082)"
                        className="p-inputtext-sm"
                    />
                    <Button
                        type="button"
                        icon="pi pi-check"
                        tooltip="Aplicar coordenada"
                        tooltipOptions={{ position: 'top' }}
                        className="p-button-success p-button-outlined"
                        onClick={aplicarCoordenadaManual}
                    />
                </div>

                <div className="flex gap-2">
                    <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={onCancelar} />
                    <Button label="Confirmar Selección" icon="pi pi-check" className="p-button-primary shadow-2" onClick={handleSave} />
                </div>
            </div>

            <small className="text-500">
                * La búsqueda pone un <b>marcador azul</b> de referencia. Haz <b>clic en el mapa</b> para marcar el punto final (rojo),
                o escribe las coordenadas en el formato <b>lat, lng</b> y presiona <b>Enter</b> o el botón verde para ubicarlas.
            </small>
        </div>
    );
}